// イベント参加者 (会員 + 非会員ゲスト) の統合表現・集計・絞り込み。
//
// サーバ (CSV 出力) とクライアント (一覧・サマリ) の両方から使う。
// 同じロジックを二重に書くと「画面の数字と CSV の数字が食い違う」事故になるため、
// ここを唯一の正本とする。
//
// 会員は event_applications、ゲストは event_guests に分かれて保存されているが、
// 表示・集計上は 1 つのリストとして扱う。

export type ParticipantKind = "member" | "guest";

/** 会員側 (event_applications) の生データ */
export interface ApplicationRow {
  /** event_applications.id。当日申込の編集・削除に使う (CSV では不要なので任意) */
  id?: string;
  memberId: string;
  memberNo: string;
  displayName: string;
  kana: string | null;
  email: string;
  applicationStatus: string;
  participationMode?: string | null;
  withSocial: boolean;
  walkIn?: boolean;
  appliedAt: string | null;
  eventCheckedInAt: string | null;
  socialCheckedInAt: string | null;
}

/** ゲスト側 (event_guests) の生データ */
export interface GuestRow {
  id: string;
  name: string;
  kana: string | null;
  categoryCode: string | null;
  categoryName: string | null;
  participationMode: string | null;
  attending: boolean;
  withSocial: boolean;
  checkedInAt: string | null;
  socialCheckedInAt: string | null;
  note: string | null;
  createdAt?: string | null;
}

/** 表示・集計用の統合表現 */
export interface Participant {
  kind: ParticipantKind;
  /** 会員は memberId、ゲストは event_guests.id */
  id: string;
  /** 会員の申込 ID (event_applications.id)。ゲストは null */
  applicationId: string | null;
  name: string;
  kana: string | null;
  memberNo: string | null;
  email: string | null;
  /** 絞り込み用のコード。会員は "member" 固定 */
  categoryCode: string;
  /** 画面・CSV 表示用。会員の当日申込は「会員 (当日)」 */
  categoryLabel: string;
  participationMode: string;
  /** キャンセル / 不参加でないか。集計の母集団になる */
  attending: boolean;
  statusLabel: string;
  withSocial: boolean;
  checkedInAt: string | null;
  socialCheckedInAt: string | null;
  walkIn: boolean;
  appliedAt: string | null;
  note: string | null;
}

/** 会員は「有効な申込」だけを参加者として数える (既存 AppSummary と同じ定義) */
export const ACTIVE_APPLICATION_STATUSES = [
  "pending_payment",
  "applied",
  "checked_in",
];

export const APPLICATION_STATUS_LABELS: Record<string, string> = {
  applied: "申込中",
  checked_in: "受付済",
  cancelled: "キャンセル",
  pending_payment: "決済待ち",
  no_show: "不参加",
};

export const PARTICIPATION_MODE_LABELS: Record<string, string> = {
  offline: "来場",
  online: "オンライン",
  hybrid: "ハイブリッド",
};

/** 会員は区分マスタを持たないため、この固定コードで扱う */
export const MEMBER_CATEGORY_CODE = "member";

export function participantFromApplication(a: ApplicationRow): Participant {
  const walkIn = a.walkIn === true;
  return {
    kind: "member",
    id: a.memberId,
    applicationId: a.id ?? null,
    name: a.displayName,
    kana: a.kana ?? null,
    memberNo: a.memberNo,
    email: a.email,
    categoryCode: MEMBER_CATEGORY_CODE,
    categoryLabel: walkIn ? "会員 (当日)" : "会員",
    participationMode: a.participationMode || "offline",
    attending: ACTIVE_APPLICATION_STATUSES.includes(a.applicationStatus),
    statusLabel: APPLICATION_STATUS_LABELS[a.applicationStatus] ?? a.applicationStatus,
    withSocial: a.withSocial,
    checkedInAt: a.eventCheckedInAt,
    socialCheckedInAt: a.socialCheckedInAt,
    walkIn,
    appliedAt: a.appliedAt,
    note: null,
  };
}

export function participantFromGuest(g: GuestRow): Participant {
  return {
    kind: "guest",
    id: g.id,
    applicationId: null,
    name: g.name,
    kana: g.kana ?? null,
    memberNo: null,
    email: null,
    categoryCode: g.categoryCode ?? "other",
    categoryLabel: g.categoryName ?? "その他",
    participationMode: g.participationMode || "offline",
    attending: g.attending,
    statusLabel: g.attending ? "参加" : "不参加",
    withSocial: g.withSocial,
    checkedInAt: g.checkedInAt,
    socialCheckedInAt: g.socialCheckedInAt,
    walkIn: false,
    appliedAt: g.createdAt ?? null,
    note: g.note,
  };
}

/** 会員 → ゲストの順に並べた統合リストを作る */
export function mergeParticipants(
  applications: ApplicationRow[],
  guests: GuestRow[],
): Participant[] {
  return [
    ...applications.map(participantFromApplication),
    ...guests.map(participantFromGuest),
  ];
}

export interface ParticipantSummary {
  /** 参加予定 (キャンセル / 不参加を除く) */
  attending: number;
  checkedIn: number;
  social: number;
  socialCheckedIn: number;
  /** 来場 (オンライン以外) */
  offline: number;
  offlineCheckedIn: number;
  offlineNotCheckedIn: number;
  online: number;
  /** キャンセル / 不参加 */
  inactive: number;
  /** 参加予定のうちゲスト / 当日申込 */
  guests: number;
  walkIns: number;
}

/**
 * 参加者を集計する。
 *
 * 受付済は必ず「参加予定」を母集団にする。
 * 母集団をそろえないと、受付後にキャンセルした人が混ざって
 * 「申込 < 受付済」の逆転が起きる (PR #5 で修正した不具合)。
 */
export function summarizeParticipants(list: Participant[]): ParticipantSummary {
  const attending = list.filter((p) => p.attending);
  const social = attending.filter((p) => p.withSocial);
  const offline = attending.filter((p) => p.participationMode !== "online");
  const offlineCheckedIn = offline.filter((p) => p.checkedInAt);
  return {
    attending: attending.length,
    checkedIn: attending.filter((p) => p.checkedInAt).length,
    social: social.length,
    socialCheckedIn: social.filter((p) => p.socialCheckedInAt).length,
    offline: offline.length,
    offlineCheckedIn: offlineCheckedIn.length,
    offlineNotCheckedIn: offline.length - offlineCheckedIn.length,
    online: attending.filter((p) => p.participationMode === "online").length,
    inactive: list.length - attending.length,
    guests: attending.filter((p) => p.kind === "guest").length,
    walkIns: attending.filter((p) => p.walkIn).length,
  };
}

export interface ParticipantFilter {
  /** 名前 / カナ / 会員番号 / メール の部分一致 */
  q: string;
  /** "" = すべて / "attending" = 参加予定のみ / それ以外は statusLabel 相当のコード */
  status: string;
  /** "" | "offline" | "online" */
  mode: string;
  /** "" | "yes" | "no" */
  social: string;
  /** "" | "done" | "yet" | "social_done" | "social_yet" */
  checkin: string;
  /** "" = すべて / "member" / 区分コード / "guest_all" = ゲスト全体 */
  category: string;
}

export const EMPTY_PARTICIPANT_FILTER: ParticipantFilter = {
  q: "",
  status: "attending",
  mode: "",
  social: "",
  checkin: "",
  category: "",
};

/** 区分絞り込みの特別値: ゲスト全体 */
export const CATEGORY_ALL_GUESTS = "guest_all";

export function filterParticipants(
  list: Participant[],
  f: ParticipantFilter,
): Participant[] {
  const q = f.q.trim().toLowerCase();
  return list.filter((p) => {
    if (q) {
      const hay = [p.name, p.kana ?? "", p.memberNo ?? "", p.email ?? ""]
        .join(" ")
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }

    if (f.status === "attending") {
      if (!p.attending) return false;
    } else if (f.status === "inactive") {
      if (p.attending) return false;
    } else if (f.status === "walk_in") {
      if (!p.walkIn) return false;
    }

    if (f.category) {
      if (f.category === CATEGORY_ALL_GUESTS) {
        if (p.kind !== "guest") return false;
      } else if (p.categoryCode !== f.category) return false;
    }

    if (f.mode === "offline" && p.participationMode === "online") return false;
    if (f.mode === "online" && p.participationMode !== "online") return false;

    if (f.social === "yes" && !p.withSocial) return false;
    if (f.social === "no" && p.withSocial) return false;

    if (f.checkin === "done" && !p.checkedInAt) return false;
    if (f.checkin === "yet" && p.checkedInAt) return false;
    if (f.checkin === "social_done" && !p.socialCheckedInAt) return false;
    if (f.checkin === "social_yet" && !(p.withSocial && !p.socialCheckedInAt))
      return false;

    return true;
  });
}
